<template>
  <div>
    <HpsfirProPageHeader
      title="驿站管理"
      title-en="STATION"
      description="维护驿站基本信息，以及各驿站绑定 / 认领的快递平台账号"
    />

    <HpsfirProStatsCards :items="statsItems" class="st-stats" />

    <HpsfirProTable
      ref="tableRef"
      :paginations="{ pageSize: 10 }"
      :columns="columns"
      :get-data="getData"
      :on-add="addFun"
      :on-edit="updateFun"
      :on-delete="deleteFun"
      :loading="loading"
      :is-dangder-delete="true"
      :operates="operates"
      :format-dangder-delete-info="formatDeleteInfo"
      add-text="添加驿站"
    />

    <!-- 绑定账号：账号天然属于某个驿站，故作为驿站的右侧抽屉承载（不是独立页面，
         也不是居中弹窗 —— 里面是一个完整的 ProTable，需要横向空间）。 -->
    <HpsfirProDrawer
      v-model:open="accountDialogVisible"
      :title="`「${currentStation.name}」的绑定账号`"
      description="账号归属该驿站；菜鸟账号需在小程序内完成短信验证后才会生效"
      :width="ACCOUNT_DRAWER_WIDTH"
      :max-width="ACCOUNT_DRAWER_WIDTH"
      content-class="st-acct-drawer"
      body-class="st-acct-body"
    >
      <HpsfirProTable
        ref="accountTableRef"
        :paginations="{ pageSize: 10 }"
        :columns="accountColumns"
        :get-data="getAccountData"
        :on-add="addAccount"
        :on-edit="updateAccount"
        :on-delete="deleteAccount"
        :loading="accountLoading"
        :is-dangder-delete="true"
        :fixed-operate="true"
        :codition-init-values="accountCondition"
        :format-dangder-delete-info="formatAccountDeleteInfo"
        add-text="绑定账号"
      />
    </HpsfirProDrawer>

    <!-- 绑定二维码：驿站拿到码后自行认领。
         二维码已改成微信「小程序码」——码面上没有可抄的文本内容，所以这里收的是
         这枚码的 **NFC 链接**（申请到 NFC 能力后由系统生成，可从列表复制），
         同时也兼容早期的明文 URL Scheme（历史物料印的是它）。 -->

    <HpsfirProForm
      ref="bindFormRef"
      :items="bindItems"
      :is-dialog="true"
      :dialog-width="560"
      title="绑定二维码"
      :description="`输入二维码对应的链接，绑定到「${currentStation.name}」`"
      :on-ok="submitBind"
      text-ok="绑定"
    />
  </div>
</template>

<script lang="ts" setup>

/**
 * 驿站管理（hpsfir-managebase-ui 1.0.0）。
 *
 * 由 Element Plus 表格改写为框架的 HpsfirProTable：
 *   - 搜索栏 / 分页 / 增改弹窗 / 删除二次确认都由 ProTable 依据 columns 自动生成；
 *   - 行内额外操作通过 operates 提供（这里是「绑定账号」）；
 *   - 绑定账号用弹窗承载（账号天然属于驿站，独立页面会让同一份数据有两个入口）。
 */
import { computed, h, ref } from 'vue';
// 注意：Hpsfir* 组件一律**不要手动 import**。
// 框架会把组件按 __name 全局注册，并且**主题会用自己的实现再注册一遍**
// （registerUiComponents → registerProComponents → registerThemeComponents，
//  同名后者覆盖前者）。手动 import 拿到的是基座/默认主题那份，
// 会绕过主题覆盖 —— 页面要用主题组件就只能靠全局注册。
// 这里只 import 非组件的东西：函数（message）、组合式函数、以及类型。
import { CalendarDays, Search, UserCheck, Users } from 'lucide-vue-next';
import {
  message,
  type HpsfirProFormInstance,
  type HpsfirProFormItem,
  type HpsfirProStatsCardItem,
  type HpsfirProTableColumn,
  type HpsfirProTableFilter,
  type HpsfirProTableInstance,
} from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';
import { renderQrImageCell, renderUrlSchemeCell } from '@/utils/qrcode-cell';

const api = useApi();

/**
 * 「绑定账号」抽屉宽度。
 *
 * 抽屉里是一个完整的 ProTable（平台类型/账号/密码/状态/创建时间/操作 + 工具栏 + 分页），
 * 原先是 1080px 的居中弹窗，表格被压得显示不全 —— 这里给足宽度，
 * 同时用 calc 兜住窄屏（抽屉是右侧滑入，不需要预留太多左侧空间）。
 */
const ACCOUNT_DRAWER_WIDTH = 'min(1280px, calc(100vw - 64px))';
const loading = ref(false);
const accountLoading = ref(false);

const tableRef = ref<HpsfirProTableInstance>();
const accountTableRef = ref<HpsfirProTableInstance>();
const bindFormRef = ref<HpsfirProFormInstance>();

const currentStation = ref<{ id?: number; name?: string }>({});
const accountDialogVisible = ref(false);

/** 与后端 PLATFORM_E 一致 */
const PLATFORM_OPTIONS = [
  { value: '菜鸟', label: '菜鸟' },
  { value: '妈妈', label: '妈妈驿站' },
  { value: '兔喜', label: '兔喜生活' },
];

// ---------------- 概览 ----------------
const summary = ref({
  todayQueryUserCount: 0,
  queryUserCount: 0,
  serveUserCount: 0,
  queryCount: 0,
});

// 统计卡片的 item 用框架的 HpsfirProStatsCardItem：icon 传的是**组件**（不是图标名字符串），
// color 用组件内已定义的 cl__color-* 类（primary / emerald / amber / destructive）。
// 不传 icon 时组件仍会渲染图标底板，看起来是个空白方块，所以 icon + color 必须成对给出。
// 参考实现：框架的「消息中心」页（views/base/_messageCenter）。
const statsItems = computed<HpsfirProStatsCardItem[]>(() => [
  { label: '今日查询人数', value: summary.value.todayQueryUserCount, icon: CalendarDays, color: 'cl__color-primary' },
  { label: '累计查询人数', value: summary.value.queryUserCount, icon: Users, color: 'cl__color-emerald' },
  { label: '累计服务人数', value: summary.value.serveUserCount, icon: UserCheck, color: 'cl__color-amber' },
  { label: '累计查询次数', value: summary.value.queryCount, icon: Search, color: 'cl__color-destructive' },
]);

const loadSummary = async() => {
  const res: any = await api.searchTrackAdminApi.summarySearchTrackAdmin();
  if (res?.data?.success && res.data.data) {
    summary.value = res.data.data;
  }
};

// ---------------- 驿站列 ----------------
const openAccounts = (row: any) => {
  currentStation.value = { id: row?.id, name: row?.name };
  accountDialogVisible.value = true;
};

const openBind = (row: any) => {
  currentStation.value = { id: row?.id, name: row?.name };
  bindFormRef.value?.reset();
  bindFormRef.value?.showDialog();
};

const bindItems = computed<HpsfirProFormItem[]>(() => [
  {
    name: 'scheme',
    label: '二维码链接',
    type: 'textaera',
    placeholder: '粘贴二维码链接：weixin://dl/business/?appid=...&path=...&query=...',
    rule: [{ required: true, message: '请输入二维码链接' }],
  },
]);

/**
 * 提交绑定。
 *
 * 后端的失败是 HTTP 4xx + 纯文本说明（未找到该链接 / 已绑定其他驿站 / 格式不正确），
 * axios 会把它抛成异常；这里把服务端那句话原样交给用户看，
 * 比统一的「请求失败」有用得多（用户需要知道到底是哪一步不对）。
 */
const submitBind = async(data: any) => {
  const stationId = currentStation.value.id;
  const scheme = String(data?.scheme || '').trim();
  try {
    const res: any = await api.searchTrackAdminApi.qrcodeBindSchemeSearchTrackAdmin({
      bindVo: { stationId, scheme },
    } as any);
    if (res?.data?.success) {
      const code = res?.data?.data?.code;
      message.success(code ? `已绑定二维码 ${code}` : '绑定成功');
      tableRef.value?.refresh();
      void loadSummary();
      return;
    }
    throw new Error(res?.data?.msg || '绑定失败');
  } catch (error: any) {
    const text = error?.response?.data;
    const detail = typeof text === 'string' && text.trim() ? text.trim() : '';
    throw new Error(detail || error?.message || '绑定失败');
  }
};

/**
 * 列宽说明（实测取值，别随手改）：
 *
 * 只要有一列声明了 width，整张表就是 `table-layout: fixed`：未声明宽度的列均分剩余宽度，
 * 内容放不下也不换行（单元格 white-space: nowrap、td overflow: visible），文字会盖到右邻列上。
 * 声明 width 之后框架才给单元格加 `--width-limited`（转 white-space: normal、
 * 内容层 overflow-wrap: anywhere），长链接才会在本列内换行。
 *
 * 列宽之和 + 操作列(300) 就是表格 min-width；1600 宽窗口下容器实测 1318px，
 * 所以合计必须 ≤ 1318，否则会出现横向滚动条（二维码管理页踩过这个坑）。
 * 本表：150+120+90+80+112+190+90+182 = 1014，+300 = 1314 ✓
 */
const columns: HpsfirProTableColumn[] = [
  {
    name: 'name',
    label: '驿站名称',
    type: 'string',
    isCondition: true,
    width: 150,
    rule: [{ required: true, message: '请输入驿站名称' }],
  },
  {
    name: 'phone',
    label: '联系电话',
    type: 'string',
    isCondition: true,
    width: 120,
    rule: [
      { required: true, message: '请输入联系电话' },
      { pattern: /^1[3-9]\d{9}$/u, message: '请输入正确的手机号' },
    ],
  },
  {
    name: 'accountCount',
    label: '绑定账号',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 90,

    // 数字可点，直接打开该驿站的账号弹窗
    render: (scope: any) => h(
      'button',
      { class: 'st-link', onClick: () => openAccounts(scope.row) },
      `${scope.row?.accountCount ?? 0}`,
    ),
  },
  {
    name: 'qrcodeCount',
    label: '二维码',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 80,
  },
  // 驿站专属码的两列：建站时自动生成、不进二维码列表，「下载二维码」下载的就是它
  {
    name: 'qrImageUrl',
    label: '二维码图片',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 112,
    render: (scope: any) => renderQrImageCell(scope, api.baseUrl, 72),
  },
  {
    name: 'urlScheme',
    label: 'NFC 链接',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 190,
    render: renderUrlSchemeCell,
  },
  {
    name: 'enabled',
    label: '状态',
    type: 'swtich',
    hideCreate: true,
    width: 90,
    trueText: '启用',
    falseText: '停用',
    handleSwitch: async(val: boolean, row: any) => {
      const res: any = await api.searchTrackAdminApi.stationUpdateSearchTrackAdmin({
        stationVo: { ...row, enabled: val ? 1 : 0 },
      } as any);
      if (res?.data?.success) {
        message.success(val ? '已启用' : '已停用');
        tableRef.value?.refresh();
      } else {
        message.error(res?.data?.msg || '操作失败');
        tableRef.value?.refresh();
      }
    },
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    width: 182,
  },
];

const formatDeleteInfo = (row: any) => `您正在进行删除驿站（${row?.name || ''}）操作，其下的账号与二维码会一并清理，请谨慎操作`;

// ---------------- 账号弹窗 ----------------
const accountCondition = computed(() => ({ stationId: currentStation.value.id }));


const accountColumns: HpsfirProTableColumn[] = [
  {
    name: 'platform',
    label: '平台类型',
    type: 'select',
    options: PLATFORM_OPTIONS,
    isCondition: true,
    rule: [{ required: true, message: '请选择平台类型' }],
  },
  {
    name: 'account',
    label: '账号',
    type: 'string',
    isCondition: true,
    rule: [{ required: true, message: '请输入账号' }],
  },
  {
    // 按业务要求：平台账号密码不打码，直接明文展示与外发
    name: 'password',
    label: '密码',
    type: 'string',
    rule: [{ required: true, message: '请输入密码' }],
  },
  {
    name: 'status',
    label: '状态',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
    // 掉线只标记不删除，这里如实显示，便于管理员核对
    render: (scope: any) => (Number(scope.row?.sessionStatus) === 3 ? '已掉线' : '正常'),
  },
  {
    name: 'createTime',
    label: '创建时间',
    type: 'string',
    hideCreate: true,
    hideEdit: true,
  },
];

const formatAccountDeleteInfo = (row: any) => `您正在进行删除账号（${row?.platform || ''} / ${row?.account || ''}）操作，请谨慎操作`;

const operates = [
  {
    key: 'accounts',
    name: '绑定账号',
    handler: (row: any) => openAccounts(row),
  },
  {
    key: 'bindQrcode',
    name: '绑定二维码',
    handler: (row: any) => openBind(row),
  },
];

// ---------------- 数据 ----------------
const getData = async(filter: HpsfirProTableFilter) => {
  loading.value = true;
  try {
    const keyword = filter.conditions?.name || filter.conditions?.phone || undefined;
    const res: any = await api.searchTrackAdminApi.stationPageSearchTrackAdmin(
      keyword,
      filter.paginations.page,
      filter.paginations.pageSize,
    );
    void loadSummary();
    return {
      total: res?.data?.data?.total || 0,
      data: res?.data?.data?.data || [],
    };
  } finally {
    loading.value = false;
  }
};

const getAccountData = async(filter: HpsfirProTableFilter) => {
  accountLoading.value = true;
  try {
    const stationId = currentStation.value.id;
    if (!stationId) {
      return { total: 0, data: [] };
    }
    const res: any = await api.searchTrackAdminApi.accountPageSearchTrackAdmin(
      filter.conditions?.account || undefined,
      stationId,
      filter.paginations.page,
      filter.paginations.pageSize,
    );
    return {
      total: res?.data?.data?.total || 0,
      data: res?.data?.data?.data || [],
    };
  } finally {
    accountLoading.value = false;
  }
};

const addFun = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.stationAddSearchTrackAdmin({
    stationVo: { ...data, enabled: 1 },
  } as any);
  if (res?.data?.success) {
    message.success('添加成功');
  } else {
    throw new Error(res?.data?.msg || '添加失败');
  }
};

const updateFun = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.stationUpdateSearchTrackAdmin({
    stationVo: data,
  } as any);
  if (res?.data?.success) {
    message.success('修改成功');
  } else {
    throw new Error(res?.data?.msg || '修改失败');
  }
};

const deleteFun = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.stationDeleteSearchTrackAdmin(data.id);
  if (res?.data?.success) {
    message.success('删除成功');
    await loadSummary();
  } else {
    throw new Error(res?.data?.msg || '删除失败');
  }
};

// ---------------- 账号增删改 ----------------
const addAccount = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.accountAddSearchTrackAdmin({
    accountVo: { ...data, stationId: currentStation.value.id },
  } as any);
  if (res?.data?.success) {
    message.success('绑定成功');
    await loadSummary();
  } else {
    throw new Error(res?.data?.msg || '绑定失败');
  }
};

const updateAccount = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.accountUpdateSearchTrackAdmin({
    accountVo: { ...data, stationId: currentStation.value.id },
  } as any);
  if (res?.data?.success) {
    message.success('修改成功');
  } else {
    throw new Error(res?.data?.msg || '修改失败');
  }
};

const deleteAccount = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.accountDeleteSearchTrackAdmin(data.id);
  if (res?.data?.success) {
    message.success('删除成功');
    tableRef.value?.refresh();
    await loadSummary();
  } else {
    throw new Error(res?.data?.msg || '删除失败');
  }
};
</script>

<style lang="scss" scoped>
.st-acct-body {
  // 抽屉 body 默认 padding: 2rem 1.5rem，内部又是带自身 gutter 的 ProTable，
  // 双重留白会让表格可用宽度变小 —— 这里交给 ProTable 自己控制留白。
  padding: 1rem 0 0;
}

.st-stats {
  // 左右留白与页头、表格对齐：主题里页头与表格内容的 gutter 都是
  // 12px（<768）/ 16px（768–1023）/ 24px（≥1024），所以这里用同一组值，
  // 卡片左右边缘才能和表格左右边缘对齐（之前没写左右 margin，卡片直接顶到容器边缘）。
  margin: 16px 12px;

  @media (min-width: 768px) {
    margin: 20px 16px 16px;
  }

  @media (min-width: 1024px) {
    margin: 24px 24px 16px;
  }
}

.st-link {
  background: none;
  border: none;
  padding: 0;
  color: var(--primary, #5b9bd5);
  cursor: pointer;
  text-decoration: underline;
}

</style>
